import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Copy, Link as LinkIcon, RotateCcw, Save, Trash2, XCircle } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { CountryCombobox } from '@/components/CountryCombobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { StateProvinceField } from '@/components/StateProvinceField'
import { useToast } from '@/hooks/useToast'
import { useDirty } from '@/hooks/useDirty'
import {
  useCustomer, useCreateCustomer, useUpdateCustomer, useDeactivateCustomer, useReactivateCustomer,
  useIssuePortalToken, useRevokePortalToken,
} from '@/features/invoicing/queries'
import { useAuth } from '@/auth/useAuth'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import type { CustomerPayload } from '@/features/invoicing/types'

function blankPayload(country: string, currency: string): CustomerPayload {
  return {
    display_name: '',
    email: '',
    phone: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: country,
    tax_id: '',
    default_payment_terms_days: 30,
    default_currency: currency,
    notes: '',
  }
}

export function CustomerEditorPage() {
  const params = useParams<{ id?: string }>()
  const isEdit = !!params.id
  const customerId = isEdit ? parseInt(params.id as string, 10) : undefined
  const navigate = useNavigate()
  const { toast } = useToast()

  const existing = useCustomer(customerId)
  const createMut = useCreateCustomer()
  const updateMut = useUpdateCustomer()
  const deactivateMut = useDeactivateCustomer()
  const reactivateMut = useReactivateCustomer()
  const issuePortalMut = useIssuePortalToken()
  const revokePortalMut = useRevokePortalToken()
  const { hasPermission } = useAuth()
  const regional = useRegionalDefaults()

  const [form, setForm] = useState<CustomerPayload>(() =>
    blankPayload(regional.country, regional.currency),
  )

  // When the tenant settings load *after* the form mounted, snap country +
  // currency to the tenant defaults exactly once. After that the user's
  // pick is sticky. Edit path skips this entirely.
  const regionalSynced = useRef(false)
  useEffect(() => {
    if (isEdit) return
    if (!regional.isLoaded || regionalSynced.current) return
    setForm((prev) => ({
      ...prev,
      billing_country: regional.country,
      default_currency: regional.currency,
    }))
    regionalSynced.current = true
  }, [isEdit, regional.isLoaded, regional.country, regional.currency])
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [portalUrl, setPortalUrl] = useState<string | null>(null)

  // Baseline for dirty-checking. On a brand-new customer the baseline is null
  // so Save is enabled as soon as the user types a name. On edit it mirrors
  // the saved customer record so Save lights up only after an actual change.
  const baseline: CustomerPayload | null = !isEdit
    ? null
    : existing.data?.customer
      ? {
          display_name: existing.data.customer.display_name,
          email: existing.data.customer.email || '',
          phone: existing.data.customer.phone || '',
          billing_address_line1: existing.data.customer.billing_address_line1 || '',
          billing_address_line2: existing.data.customer.billing_address_line2 || '',
          billing_city: existing.data.customer.billing_city || '',
          billing_state: existing.data.customer.billing_state || '',
          billing_postal_code: existing.data.customer.billing_postal_code || '',
          billing_country: existing.data.customer.billing_country || '',
          tax_id: existing.data.customer.tax_id || '',
          default_payment_terms_days: existing.data.customer.default_payment_terms_days,
          default_currency: existing.data.customer.default_currency,
          notes: existing.data.customer.notes || '',
        }
      : null
  const dirty = useDirty(form, baseline)

  useEffect(() => {
    if (!isEdit || !existing.data?.customer) return
    const c = existing.data.customer
    setForm({
      display_name: c.display_name,
      email: c.email || '',
      phone: c.phone || '',
      billing_address_line1: c.billing_address_line1 || '',
      billing_address_line2: c.billing_address_line2 || '',
      billing_city: c.billing_city || '',
      billing_state: c.billing_state || '',
      billing_postal_code: c.billing_postal_code || '',
      billing_country: c.billing_country || '',
      tax_id: c.tax_id || '',
      default_payment_terms_days: c.default_payment_terms_days,
      default_currency: c.default_currency,
      notes: c.notes || '',
    })
  }, [isEdit, existing.data])

  function setField<K extends keyof CustomerPayload>(k: K, v: CustomerPayload[K]) {
    setForm(prev => ({ ...prev, [k]: v }))
  }

  async function onSave() {
    if (!form.display_name?.trim()) {
      toast({ title: 'Customer name is required', variant: 'destructive' })
      return
    }
    try {
      if (isEdit && customerId) {
        // Round-trip the updated_at the form loaded so the backend can
        // detect a concurrent edit. On 409 STALE_VERSION the global
        // StaleVersionToast prompts a reload; we still surface the
        // toast here so the user sees it inside the form too.
        await updateMut.mutateAsync({
          id: customerId,
          body: { ...form, expected_updated_at: existing.data?.customer?.updated_at ?? null },
        })
        toast({ title: 'Customer updated' })
      } else {
        await createMut.mutateAsync(form)
        toast({ title: 'Customer created' })
      }
      navigate('/invoicing/customers')
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Save failed', variant: 'destructive' })
    }
  }

  async function onIssuePortal() {
    if (!customerId) return
    try {
      const r = await issuePortalMut.mutateAsync(customerId)
      setPortalUrl(r.url)
      toast({ title: 'Portal link issued - copy it now.' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Failed to issue portal link', variant: 'destructive' })
    }
  }
  async function onRevokePortal() {
    if (!customerId) return
    try {
      await revokePortalMut.mutateAsync(customerId)
      setPortalUrl(null)
      toast({ title: 'Portal link revoked.' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Failed to revoke', variant: 'destructive' })
    }
  }
  async function onCopyPortal() {
    if (!portalUrl) return
    try {
      await navigator.clipboard.writeText(portalUrl)
      toast({ title: 'Copied to clipboard.' })
    } catch {
      toast({ title: 'Copy failed - select and copy the URL manually.', variant: 'destructive' })
    }
  }

  async function onDeactivateConfirm() {
    if (!customerId) return
    try {
      await deactivateMut.mutateAsync(customerId)
      setDeactivateOpen(false)
      toast({ title: 'Customer deactivated' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Failed', variant: 'destructive' })
    }
  }

  async function onReactivate() {
    if (!customerId) return
    try {
      await reactivateMut.mutateAsync(customerId)
      toast({ title: 'Customer reactivated' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Failed', variant: 'destructive' })
    }
  }

  return (
    <Page
      title={isEdit ? form.display_name || 'Customer' : 'New customer'}
      actions={
        <Toolbar align="end">
          <Button variant="ghost" onClick={() => navigate('/invoicing/customers')}>
            <ArrowLeft className="mr-1 size-4" /> Back
          </Button>
          {isEdit && existing.data?.customer && (
            existing.data.customer.is_active ? (
              <Button variant="danger" onClick={() => setDeactivateOpen(true)}>
                <Trash2 className="mr-1 size-4" /> Deactivate
              </Button>
            ) : (
              <Button variant="secondary" onClick={onReactivate} disabled={reactivateMut.isPending}>
                <CheckCircle2 className="mr-1 size-4" /> Reactivate
              </Button>
            )
          )}
          <Button onClick={onSave} disabled={!dirty || createMut.isPending || updateMut.isPending}>
            <Save className="mr-1 size-4" /> Save
          </Button>
        </Toolbar>
      }
    >
      <Card>
        <div className="grid gap-4 p-6 md:grid-cols-2">
          <FormField
            label="Display name"
            required
            value={form.display_name || ''}
            onChange={e => setField('display_name', e.target.value)}
          />
          <FormField
            label="Email"
            type="email"
            value={form.email || ''}
            onChange={e => setField('email', e.target.value)}
            hint="Used to send invoices + hosted pay links."
          />
          <FormField label="Phone" value={form.phone || ''} onChange={e => setField('phone', e.target.value)} />
          <FormField label="Tax ID / VAT" value={form.tax_id || ''} onChange={e => setField('tax_id', e.target.value)} />
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="mb-3 text-sm font-semibold">Billing address</div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label="Address line 1"
              containerClassName="md:col-span-2"
              value={form.billing_address_line1 || ''}
              onChange={e => setField('billing_address_line1', e.target.value)}
            />
            <FormField
              label="Address line 2"
              containerClassName="md:col-span-2"
              value={form.billing_address_line2 || ''}
              onChange={e => setField('billing_address_line2', e.target.value)}
            />
            <FormField label="City" value={form.billing_city || ''} onChange={e => setField('billing_city', e.target.value)} />
            <FormField
              label={
                form.billing_country?.toUpperCase() === 'US'
                  ? 'State'
                  : form.billing_country?.toUpperCase() === 'CA'
                    ? 'Province'
                    : 'State / region'
              }
              control={
                <StateProvinceField
                  country={form.billing_country}
                  value={form.billing_state || ''}
                  onChange={v => setField('billing_state', v)}
                />
              }
            />
            <FormField label="Postal code" value={form.billing_postal_code || ''} onChange={e => setField('billing_postal_code', e.target.value)} />
            <FormField
              label="Country"
              hint="Picking a country auto-fills the default currency."
              control={
                <CountryCombobox
                  value={form.billing_country || null}
                  onChange={v => {
                    setField('billing_country', v || '')
                    // Clear the state/province field when the country changes -
                    // a US "CA" should not survive a flip to Canada (where it
                    // would silently mean Alberta won't match).
                    setField('billing_state', '')
                  }}
                  onCurrencyChange={c => setField('default_currency', c)}
                />
              }
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="grid gap-4 p-6 md:grid-cols-3">
          <FormField
            label="Default payment terms (days)"
            type="number"
            value={String(form.default_payment_terms_days ?? 30)}
            onChange={e => setField('default_payment_terms_days', Number(e.target.value) || 0)}
          />
          <FormField
            label="Default currency"
            control={
              <CurrencyCombobox
                value={form.default_currency || regional.currency}
                onChange={v => setField('default_currency', v)}
              />
            }
          />
          <FormField
            label="Notes"
            containerClassName="md:col-span-1"
            value={form.notes || ''}
            onChange={e => setField('notes', e.target.value)}
          />
        </div>
      </Card>

      {isEdit && hasPermission('invoicing.customer_portal.manage') && (
        <Card>
          <div className="space-y-3 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Customer portal link</div>
                <p className="text-xs text-muted-foreground">
                  One URL the customer can bookmark to see every open invoice on this account and pay each one. Issuing a new link revokes the previous URL. Portal access is read-only outside of the per-invoice pay flow.
                </p>
                {existing.data?.customer && !portalUrl ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    The full URL is only shown once, immediately after issuing - copy it from this screen and share it with the customer.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={onIssuePortal} disabled={issuePortalMut.isPending}>
                  <RotateCcw className="mr-1 size-4" />
                  {portalUrl ? 'Rotate link' : 'Issue link'}
                </Button>
                <Button variant="ghost" onClick={onRevokePortal} disabled={revokePortalMut.isPending}>
                  <XCircle className="mr-1 size-4 text-destructive" /> Revoke
                </Button>
              </div>
            </div>
            {portalUrl ? (
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <div className="flex items-center gap-2">
                  <LinkIcon className="size-4 text-muted-foreground" />
                  <a href={portalUrl} target="_blank" rel="noreferrer" className="break-all text-primary underline">
                    {portalUrl}
                  </a>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button variant="ghost" size="sm" onClick={onCopyPortal}>
                    <Copy className="mr-1 size-4" /> Copy URL
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      <Modal.Confirm
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        title="Deactivate customer?"
        body="The customer will be hidden from new-invoice pickers. Existing invoices remain intact. You can reactivate the customer from this screen at any time."
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onConfirm={onDeactivateConfirm}
        busy={deactivateMut.isPending}
      />
    </Page>
  )
}
